// ─── Reply markup, as React — the CRM's copy ────────────────────────────────
//
// Renders what lib/replyMarkup.js parsed: the case feed and the composer's
// preview both go through here, so what the rep sees before Send is what the
// customer's dashboard and the case-reply email render from the same text.
// Everything is built as ELEMENTS — never an HTML string handed to the DOM —
// so the only thing deciding what a character means is the parser.
//
// `inlineMarkup: false` is for text a CUSTOMER wrote (their dashboard notes in
// the feed): shown exactly as typed with bare URLs clickable, so a customer
// cannot dress a link up as something else in front of a rep. Links carry the
// href as their title for the same reason — hover shows where it goes.

import React from 'react';
import { parseReplyMarkup } from '../lib/replyMarkup';

const Inline = ({ nodes, linkStyle }) => nodes.map((n, i) => {
  switch (n.type) {
    case 'link':
      return (
        <a key={i} href={n.href} target="_blank" rel="noopener noreferrer" title={n.href} style={linkStyle}>
          <Inline nodes={n.children} linkStyle={linkStyle} />
        </a>
      );
    case 'strong': return <strong key={i} style={{ color: 'var(--text)' }}><Inline nodes={n.children} linkStyle={linkStyle} /></strong>;
    case 'em':     return <em key={i}><Inline nodes={n.children} linkStyle={linkStyle} /></em>;
    case 'code':   return <code key={i} style={{ fontFamily: 'var(--mono)', fontSize: '0.9em', padding: '1px 5px', borderRadius: 4, background: 'var(--bg3)', color: 'var(--text)' }}>{n.value}</code>;
    default:       return <React.Fragment key={i}>{n.value}</React.Fragment>;
  }
});

export default function ReplyMarkup({ text, inlineMarkup = true, style, linkStyle }) {
  const blocks = parseReplyMarkup(text, { inlineMarkup });
  if (blocks.length === 0) return null;
  const linkCss = { color: 'var(--accent)', textDecoration: 'underline', textUnderlineOffset: 2, wordBreak: 'break-word', ...linkStyle };
  return (
    <div style={{ wordBreak: 'break-word', ...style }}>
      {blocks.map((b, i) => {
        const gap = i === blocks.length - 1 ? 0 : '0.7em';
        if (b.type === 'list') {
          const Tag = b.ordered ? 'ol' : 'ul';
          return (
            <Tag key={i} style={{ margin: `0 0 ${gap}`, paddingLeft: 22 }}>
              {b.items.map((item, j) => <li key={j} style={{ margin: '2px 0' }}><Inline nodes={item} linkStyle={linkCss} /></li>)}
            </Tag>
          );
        }
        return (
          <p key={i} style={{ margin: `0 0 ${gap}` }}>
            {b.lines.map((line, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                <Inline nodes={line} linkStyle={linkCss} />
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
